import { Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Context } from '@lib.core/context/context';
import { LoggerService } from '@lib.core/logger/logger.service';
import { ConsoleLoggerService } from '@lib.core/logger/console-logger.service';
import { ContextStore } from '@lib.resources/context/context-store.service';
import { ClientIdQParamInteractor } from '@public/src/app/client-id-qparam.interactor';
import { LangQParamInteractor } from '@public/src/app/lang-qparam.interactor';
import { IsHeadlessQParamInteractor } from '@public/src/app/is-headless-qparam.interactor';
import { AspectViewQParamInteractor } from '@public/src/app/indicator-view-qparam.interactor';
import { FacadeKeyQParamInteractor } from '@public/src/app/facade-key-qparam.interactor';
import { VizSubtypeQParamInteractor } from '@public/src/app/viz-subtype-qparam.interactor';
import { ServiceRegistrar } from '@public/src/app/services/service-registrar';
import { environment } from '@public/src/environments/environment';
import { CountryCodeInteractor } from '@public/src/app/country-code.interactor';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly contextStorageService = inject(ContextStore);
  private readonly clientIdQParamInteractor = inject(ClientIdQParamInteractor);
  private readonly langQParamInteractor = inject(LangQParamInteractor);
  private readonly isHeadlessQParamInteractor = inject(IsHeadlessQParamInteractor);
  private readonly aspectViewQParamInteractor = inject(AspectViewQParamInteractor);
  private readonly facadeKeyQParamInteractor = inject(FacadeKeyQParamInteractor);
  private readonly vizSubtypeQParamInteractor = inject(VizSubtypeQParamInteractor);
  private readonly serviceRegistrar = inject(ServiceRegistrar);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly countryCodeInteractor = inject(CountryCodeInteractor);
  private readonly defaultLanguage = 'en';
  private readonly logger = LoggerService.getLoggerService();
  private readonly ctx: WritableSignal<Context> = signal(new Context('application'));

  constructor() {
    this.translateService.use(this.defaultLanguage);
    this.ctx.update(ctx => {
      ctx.lang = this.defaultLanguage;
      return ctx;
    });
    this.contextStorageService.setContext(this.ctx());
    this.clientIdQParamInteractor.readClientIdFromQParam();
    this.langQParamInteractor.readLangFromQParam();
    this.isHeadlessQParamInteractor.readIsHeadlessFromQParam();
    this.aspectViewQParamInteractor.readAspectViewFromQParam();
    this.facadeKeyQParamInteractor.readFacadeKeyFromQParam();
    this.vizSubtypeQParamInteractor.readVizSubtypeFromQParam();
    this.countryCodeInteractor.setGlobalCountryCode();
    this.serviceRegistrar.register();
    this.consoleLoggerService.setMinLoggingLevel(environment.defaultConsoleMinLoggingLevel);
    this.logger.addLogger(this.consoleLoggerService);
  }

  ngOnInit(): void {
    this.logger.debug('Localintel3 Public Application');
  }

  ngOnDestroy() {
    this.clientIdQParamInteractor.destroy();
    this.langQParamInteractor.destroy();
    this.isHeadlessQParamInteractor.destroy();
    this.aspectViewQParamInteractor.destroy();
    this.facadeKeyQParamInteractor.destroy();
    this.vizSubtypeQParamInteractor.destroy();
  }
}
